import { collection, doc, getDoc, getDocs, query, where, orderBy, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseconfig';

// Link expense to stock transactions
export const linkExpenseToStockTransactions = async (expenseId, stockTransactionIds, stockItemsUsed) => {
  try {
    const expenseRef = doc(db, 'expenses', expenseId);
    await updateDoc(expenseRef, {
      linkedStockTransactionIds: stockTransactionIds,
      stockItemsUsed: stockItemsUsed,
      isStockRelated: true,
      updatedAt: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error('Error linking expense to stock transactions:', error);
    throw error;
  }
};

// Link stock transaction to expense
export const linkStockTransactionToExpense = async (stockTransactionId, expenseId) => {
  try {
    const stockTransactionRef = doc(db, 'stock_transactions', stockTransactionId);
    await updateDoc(stockTransactionRef, {
      linkedExpenseId: expenseId,
      updatedAt: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error('Error linking stock transaction to expense:', error);
    throw error;
  }
};

// Get expense with linked stock transactions
export const getExpenseWithStock = async (expenseId) => {
  try {
    const expenseDoc = await getDoc(doc(db, 'expenses', expenseId));
    if (!expenseDoc.exists()) {
      return null;
    }

    const expense = { id: expenseDoc.id, ...expenseDoc.data() };
    
    if (expense.linkedStockTransactionIds && expense.linkedStockTransactionIds.length > 0) {
      const stockTransactions = [];
      for (const transactionId of expense.linkedStockTransactionIds) {
        const transactionDoc = await getDoc(doc(db, 'stock_transactions', transactionId));
        if (transactionDoc.exists()) {
          stockTransactions.push({
            id: transactionDoc.id,
            ...transactionDoc.data()
          });
        }
      }
      expense.linkedStockTransactions = stockTransactions;
    }

    return expense;
  } catch (error) {
    console.error('Error fetching expense with stock:', error);
    throw error;
  }
};

// Get stock transaction with linked expense
export const getStockTransactionWithExpense = async (transactionId) => {
  try {
    const transactionDoc = await getDoc(doc(db, 'stock_transactions', transactionId));
    if (!transactionDoc.exists()) {
      return null;
    }

    const transaction = { id: transactionDoc.id, ...transactionDoc.data() };
    
    if (transaction.linkedExpenseId) {
      const expenseDoc = await getDoc(doc(db, 'expenses', transaction.linkedExpenseId));
      if (expenseDoc.exists()) {
        transaction.linkedExpense = {
          id: expenseDoc.id,
          ...expenseDoc.data()
        };
      }
    }

    return transaction;
  } catch (error) {
    console.error('Error fetching stock transaction with expense:', error);
    throw error;
  }
};

// Calculate cost per unit from expense
export const calculateCostPerUnit = (expenseAmount, totalStockQuantity) => {
  if (!totalStockQuantity || totalStockQuantity === 0) {
    return 0;
  }
  return expenseAmount / totalStockQuantity;
};

// Calculate inventory valuation - now simply uses currentBalance
export const calculateInventoryValuation = async (stockItems) => {
  try {
    let totalValuation = 0;
    const itemValuations = [];

    for (const item of stockItems) {
      const balance = item.currentBalance || 0;
      totalValuation += balance;

      itemValuations.push({
        itemId: item.id,
        itemName: item.name,
        balance: balance,
        value: balance
      });
    }

    return {
      totalValuation,
      itemValuations
    };
  } catch (error) {
    console.error('Error calculating inventory valuation:', error);
    throw error;
  }
};

// Calculate cost of goods sold (COGS) - now simply sums stock_out expense amounts
export const calculateCOGS = async (startDate, endDate) => {
  try {
    const expensesRef = collection(db, 'expenses');
    let q = query(
      expensesRef,
      where('expenseType', '==', 'stock_out'),
      orderBy('createdAt', 'asc')
    );

    const snapshot = await getDocs(q);
    let stockOutExpenses = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate?.() || new Date(doc.data().date),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
    }));

    // Filter by date range
    if (startDate && endDate) {
      stockOutExpenses = stockOutExpenses.filter(expense => {
        const expenseDate = new Date(expense.date || expense.createdAt);
        return expenseDate >= startDate && expenseDate <= endDate;
      });
    }

    let totalCOGS = 0;
    const cogsByItem = {};

    for (const expense of stockOutExpenses) {
      const amount = expense.amount || 0;
      totalCOGS += amount;

      if (expense.stockItemId) {
        if (!cogsByItem[expense.stockItemId]) {
          cogsByItem[expense.stockItemId] = {
            itemId: expense.stockItemId,
            cost: 0
          };
        }
        cogsByItem[expense.stockItemId].cost += amount;
      }
    }

    // Get item names
    const stockItemsSnapshot = await getDocs(collection(db, 'stock_items'));
    const stockItemsMap = new Map(stockItemsSnapshot.docs.map(d => [d.id, d.data().name]));

    const cogsByItemList = Object.values(cogsByItem).map(item => ({
      ...item,
      itemName: stockItemsMap.get(item.itemId) || 'Unknown Item'
    }));

    return {
      totalCOGS,
      cogsByItem: cogsByItemList,
      transactionCount: stockOutExpenses.length
    };
  } catch (error) {
    console.error('Error calculating COGS:', error);
    throw error;
  }
};

// Get expenses that use stock (stock_in or stock_out)
export const getExpensesWithStock = async (startDate, endDate) => {
  try {
    const expensesRef = collection(db, 'expenses');
    let q = query(
      expensesRef,
      where('expenseType', 'in', ['stock_in', 'stock_out']),
      orderBy('createdAt', 'desc')
    );

    if (startDate && endDate) {
      q = query(
        expensesRef,
        where('expenseType', 'in', ['stock_in', 'stock_out']),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate?.() || new Date(doc.data().date),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
    }));
  } catch (error) {
    console.error('Error fetching expenses with stock:', error);
    throw error;
  }
};

// Get stock usage report - now returns expenses for the item
export const getStockUsageReport = async (itemId, startDate, endDate) => {
  try {
    const expensesRef = collection(db, 'expenses');
    let q = query(
      expensesRef,
      where('stockItemId', '==', itemId),
      where('expenseType', 'in', ['stock_in', 'stock_out']),
      orderBy('createdAt', 'desc')
    );

    if (startDate && endDate) {
      q = query(
        expensesRef,
        where('stockItemId', '==', itemId),
        where('expenseType', 'in', ['stock_in', 'stock_out']),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate?.() || new Date(doc.data().date),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
    }));
  } catch (error) {
    console.error('Error generating stock usage report:', error);
    throw error;
  }
};

